/**
 * Shared style constants for consistent theming and responsive design
 */

// Base container styles
export const PANEL_STYLES = 'bg-white dark:bg-gray-800 rounded-lg shadow p-2 flex-shrink-0';
export const OVERLAY_STYLES = 'absolute inset-0 bg-black/50 flex items-center justify-center z-50 p-4';

// Text styles
export const HEADING_STYLES = 'text-xs font-bold text-gray-900 dark:text-white';
export const SUBHEADING_STYLES = 'text-xs text-gray-600 dark:text-gray-400';
export const LABEL_STYLES = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

// Button base styles
export const BUTTON_BASE = 'font-semibold rounded transition-colors shadow active:scale-95';
export const BUTTON_PRIMARY = `${BUTTON_BASE} bg-primary-500 hover:bg-primary-600 text-white`;
export const BUTTON_SECONDARY = `${BUTTON_BASE} bg-blue-500 hover:bg-blue-600 text-white`;
export const BUTTON_DANGER = `${BUTTON_BASE} bg-red-500 hover:bg-red-600 text-white`;
export const BUTTON_DISABLED = 'disabled:bg-surface-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed';

// Increment/decrement button styles
export const CONTROL_BUTTON = 'w-8 h-8 rounded bg-primary-500 hover:bg-primary-600 disabled:bg-surface-300 dark:disabled:bg-gray-600 text-white font-bold text-sm shadow active:scale-95 disabled:cursor-not-allowed';

// Player card styles
export const PLAYER_CARD_BASE = 'p-2 rounded border flex-shrink-0';
export const PLAYER_CARD_SELF = 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700';
export const PLAYER_CARD_OTHER = 'bg-surface-50 dark:bg-gray-700 border-surface-200 dark:border-gray-600';
export const PLAYER_CARD_ACTIVE = 'ring-2 ring-secondary-400 dark:ring-secondary-500';

// Overlay card styles
export const CHALLENGE_OVERLAY_CARD = 'bg-amber-50 dark:bg-amber-900/90 border-2 border-amber-400 dark:border-amber-600 rounded-xl p-4 max-w-lg max-h-[90vh] overflow-y-auto';
export const GAME_OVER_OVERLAY_CARD = 'bg-primary-50 dark:bg-primary-900/90 border-2 border-primary-500 dark:border-primary-400 rounded-xl p-6 text-center';

// Challenge overlay text styles
export const CHALLENGE_TITLE = 'text-lg font-bold text-amber-900 dark:text-amber-100 mb-1';
export const CHALLENGE_TEXT = 'text-sm text-amber-800 dark:text-amber-200';
export const CHALLENGE_HIGHLIGHT = 'text-base font-bold text-amber-900 dark:text-amber-100';

// Size variants
export const BUTTON_SIZES = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
} as const;

// Helper function to combine button styles
export const getButtonClasses = (
  variant: 'primary' | 'secondary' | 'danger',
  size: keyof typeof BUTTON_SIZES = 'md',
  disabled = false
): string => {
  const variantMap = {
    primary: BUTTON_PRIMARY,
    secondary: BUTTON_SECONDARY,
    danger: BUTTON_DANGER,
  };

  return `${variantMap[variant]} ${BUTTON_SIZES[size]} ${disabled ? BUTTON_DISABLED : ''}`.trim();
};